import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Toolbar,
  Button,
  Grid,
  TextField,
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import moment from 'moment';

import AccountAutocomplete from './AccountAutocomplete';
import AnalyticsFacetAutocomplete from './AnalyticsFacetAutocomplete';
import RebuildReadModelButton from './RebuildReadModelButton';

const useStyles = makeStyles(theme => ({
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  title: {
    flex: '1 1 100%',
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    justifyContent: 'flex-end',
  },
}));

// The applied filter shape mirrors the /v3/analytics query params. Empty strings/nulls
// are dropped by the view before the request is built, so partial filtering just works.
const EMPTY_FILTERS = {
  accountEmail: '',
  profileEmail: '',
  accountUuid: '',
  type: '',
  key: '',
  applicationName: '',
  userAgent: '',
  from: null,
  to: null,
};

const AnalyticsToolbar = props => {
  const { onApply } = props;
  const classes = useStyles();
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  // The account chosen in the autocomplete; kept for display, while its UUID drives the filter.
  const [account, setAccount] = useState(null);

  const setField = field => event =>
    setDraft({ ...draft, [field]: event.target.value });

  const setValue = field => value =>
    setDraft(current => ({ ...current, [field]: value }));

  const setDate = field => date =>
    setDraft({ ...draft, [field]: date });

  const selectAccount = selection => {
    setAccount(selection);
    setDraft(current => ({ ...current, accountUuid: selection ? selection.accountUuid : '' }));
  };

  const apply = () => onApply(draft);
  const clear = () => {
    setAccount(null);
    setDraft(EMPTY_FILTERS);
    onApply(EMPTY_FILTERS);
  };

  const textField = (field, label) => (
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label={label}
        value={draft[field]}
        onChange={setField(field)}
        onKeyDown={event => { if (event.key === 'Enter') apply(); }}
      />
    </Grid>
  );

  return (
    <Card className={classes.card}>
      <Toolbar className={classes.toolbar}>
        <Typography className={classes.title} variant="h4" component="div">
          Analytics
        </Typography>
        <RebuildReadModelButton />
      </Toolbar>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <AccountAutocomplete label="Account" selected={account} onSelect={selectAccount} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <AnalyticsFacetAutocomplete
              label="Type"
              field="type"
              value={draft.type}
              onChange={setValue('type')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <AnalyticsFacetAutocomplete
              label="Key"
              field="key"
              value={draft.key}
              onChange={setValue('key')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <AnalyticsFacetAutocomplete
              label="Application name"
              field="applicationName"
              value={draft.applicationName}
              onChange={setValue('applicationName')}
            />
          </Grid>
          {textField('userAgent', 'User agent')}
          <Grid item xs={12} sm={6} md={4}>
            <KeyboardDatePicker
              autoOk
              fullWidth
              margin="none"
              size="small"
              inputVariant="outlined"
              label="From"
              format={moment.localeData().longDateFormat('L')}
              value={draft.from}
              onChange={setDate('from')}
              disableFuture
              KeyboardButtonProps={{ 'aria-label': 'change from date' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KeyboardDatePicker
              autoOk
              fullWidth
              margin="none"
              size="small"
              inputVariant="outlined"
              label="Until"
              format={moment.localeData().longDateFormat('L')}
              value={draft.to}
              onChange={setDate('to')}
              disableFuture
              KeyboardButtonProps={{ 'aria-label': 'change until date' }}
            />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions className={classes.actions}>
        <Button onClick={clear}>Clear</Button>
        <Button color="primary" variant="contained" onClick={apply}>Apply</Button>
      </CardActions>
    </Card>
  );
};

AnalyticsToolbar.propTypes = {
  onApply: PropTypes.func.isRequired,
};

export default AnalyticsToolbar;